import { DISCOUNT_TYPES } from 'constants/subscriptions';

import ApiClient from 'services/ApiClient';

import {
    ICancelSubscriptionRequest,
    IChangeSubscription,
    IDiscountSubscription,
    IPauseSubscription,
    IReminderOffer,
    IRestoreSubscription,
    IResumeSubscription,
    IReturnSubscription,
    ISubscription,
    ISubscriptionFeedback,
    PaymentProviders,
} from 'types/subscription';

import { IMessageResponse, IResultResponse } from '../types/response';

class SubscriptionsApi {
    getSubscriptions = async <Response = ISubscription>(): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.get<Response>('user/subscription');
    };

    unsubscribe = async <Data = ICancelSubscriptionRequest, Response = IResultResponse>(
        payload: Data
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post<Data, Response>('subscriptions/cancel', payload);
    };

    sendFeedback = async <Data = ISubscriptionFeedback, Response = IMessageResponse>(
        payload: Data
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post<Data, Response>('feedback/', payload);
    };

    changeSubscription = async <Data = IChangeSubscription, Response = IResultResponse>(
        payload: Data
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post<Data, Response>('subscriptions/switch', payload);
    };

    getDiscountSubscriptions = async <
        Data = {
            external_id: string;
            discount_type: DISCOUNT_TYPES;
            provider: PaymentProviders;
        },
        Response = IDiscountSubscription,
    >(
        payload: Data
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post<typeof payload, Response>('products/discount', payload);
    };

    restoreSubscription = async <Data = IRestoreSubscription, Response = IResultResponse>(
        payload: Data
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post<Data, Response>('subscriptions/restore', payload);
    };

    returnSubscription = async <Response = string>(
        payload: IReturnSubscription
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post('subscriptions/reset', payload);
    };

    pauseSubscription = async <Data = IPauseSubscription, Response = string>(
        payload: Data
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post<Data, Response>('subscriptions/pause', payload);
    };

    resumeSubscription = async <Data = IResumeSubscription, Response = string>(
        payload: Data
    ): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post<Data, Response>('subscriptions/resume', payload);
    };

    setReminder = async <Response = string>(payload: IReminderOffer): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post('user/subscription/reminder', payload);
    };

    changePlan = async <Response = string>(payload: string): Promise<Partial<Response> | unknown> => {
        return await ApiClient.private.post('plan-settings', { plan_name: payload });
    };
}

export default SubscriptionsApi;
